<script setup>
import { ref, computed, onMounted, onUnmounted } from 'vue';
import { useChatStore } from '../../stores/chat';

const chatStore = useChatStore();

const showTooltip = ref(false);
const tooltipPosition = ref({ x: 0, y: 0 });

const resetDateText = computed(() => {
  if (!chatStore.quota.resetDate) return 'N/A';
  return new Date(chatStore.quota.resetDate).toLocaleDateString();
});

const quotaLevel = computed(() => {
  return chatStore.getQuotaLevel();
});

const handleMouseEnter = (event) => {
  if (chatStore.quota.isPremium) return; // Don't show tooltip for premium users
  
  showTooltip.value = true;
  updateTooltipPosition(event);
};

const handleMouseLeave = () => {
  showTooltip.value = false;
};

const updateTooltipPosition = (event) => {
  const rect = event.target.getBoundingClientRect();
  tooltipPosition.value = {
    x: rect.left - 125,
    y: rect.top - 40
  };
};

// Hide tooltip when clicking outside
const handleClickOutside = (event) => {
  if (!event.target.closest('.dataagent-quota-tooltip')) {
    showTooltip.value = false;
  }
};

onMounted(() => {
  document.addEventListener('click', handleClickOutside);
});

onUnmounted(() => {
  document.removeEventListener('click', handleClickOutside);
});
</script>

<template>
  <div v-if="!chatStore.quota.isPremium" class="dataagent-quota-usage da:flex da:items-center da:space-x-3 da:text-sm">
    <!-- Quota Info -->
    <div class="dataagent-quota-info da:flex da:items-center da:space-x-2">
      <span class="dashicons dashicons-chart-pie da:text-gray-500"></span>
      <span class="da:text-gray-700 da:font-medium">{{ chatStore.getQuotaUsageText }}</span>
    </div>

    <!-- Tooltip Trigger -->
    <div 
      class="dataagent-quota-tooltip da:relative da:cursor-help"
      @mouseenter="handleMouseEnter"
      @mouseleave="handleMouseLeave"
    >
      <span class="dashicons dashicons-info da:text-gray-400 da:hover:text-gray-600 da:transition-colors"></span>
      
      <!-- Tooltip Content -->
      <div 
        v-if="showTooltip"
        class="tooltip-text da:absolute da:bg-gray-800 da:text-white da:text-xs da:px-3 da:py-2 da:rounded-lg da:shadow-lg da:z-50 da:whitespace-nowrap"
        :style="{ 
          left: tooltipPosition.x + 'px', 
          top: tooltipPosition.y + 'px',
          visibility: showTooltip ? 'visible' : 'hidden',
          opacity: showTooltip ? '1' : '0'
        }"
      >
        Monthly usage: {{ chatStore.quota.used }} out of {{ chatStore.quota.total }} requests. 
        <template v-if="chatStore.quota.resetDate">Reset date: {{ resetDateText }}</template>
        <div class="da:absolute da:top-full da:left-1/2 da:transform da:-translate-x-1/2 da:w-0 da:h-0 da:border-l-4 da:border-r-4 da:border-t-4 da:border-transparent da:border-t-gray-800"></div>
      </div>
    </div>

    <!-- Progress Bar -->
    <div class="dataagent-quota-progress da:w-24 da:bg-gray-200 da:rounded-full da:h-2 da:ml-2">
      <div 
        class="da:h-2 da:rounded-full da:transition-all da:duration-300"
        :class="{
          'da:bg-green-500': quotaLevel === 'low',
          'da:bg-yellow-500': quotaLevel === 'medium',
          'da:bg-red-500': quotaLevel === 'high'
        }"
        :style="{ width: chatStore.getQuotaPercentage + '%' }"
      ></div>
    </div>
  </div>

  <!-- Premium Badge -->
  <div v-else class="dataagent-quota-usage da:flex da:items-center da:space-x-2 da:text-sm">
    <span class="dashicons dashicons-star-filled da:text-green-500"></span>
    <span class="da:text-gray-700 da:font-medium">{{ chatStore.labels.premium_plan || 'Premium Plan' }}</span>
  </div>
</template>